import React, { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, ArrowRight, Plus, Trash2, Save, Eye, Copy, Check, Zap, Loader2, ExternalLink, Upload, Image, Info, Ruler, Package, Sparkles } from 'lucide-react';
import * as LucideIcons from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { businessTemplates, formatPrice, generateSlug, marcenariaAmbientes, marcenariaMateriais, marcenariaAdicionais, marcenariaTextoTransparencia } from '../data/orcamentoMock';
import { siteData } from '../data/mock';
import MasterAccess from '../components/MasterAccess';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const OrcamentoConfigurar = () => {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [copied, setCopied] = useState(false);
  const [loading, setLoading] = useState(false);
  const [paymentLink, setPaymentLink] = useState(null);
  const [paymentError, setPaymentError] = useState(null);
  const logoInputRef = useRef(null);
  
  // Form state
  const [formData, setFormData] = useState({
    name: '',
    business: '',
    phone: '',
    email: '',
    cpfCnpj: '',
    logo: null, // Base64 da logo
    logoPreview: null, // URL para preview
    template: null,
    services: [],
    // Configuração de Marcenaria (quando template é marcenaria)
    isMarcenaria: false,
    marcenariaConfig: {
      ambientes: marcenariaAmbientes.map(a => ({ ...a, ativo: true })),
      materiais: marcenariaMateriais.map(m => ({ ...m, preco: m.precoSugerido, ativo: true })),
      adicionais: marcenariaAdicionais.map(a => ({ ...a, valor: a.valorSugerido, ativo: true })),
      ambientesCustom: [],
      adicionaisCustom: [],
      textoTransparencia: marcenariaTextoTransparencia
    },
    // Payment config for receiving from clients
    paymentMethod: '', // 'asaas', 'pix', 'presencial'
    pixKey: '',
    pixKeyType: 'cpf', // cpf, cnpj, email, phone, random
    asaasAccount: '' // se tiver conta Asaas
  });

  const [newService, setNewService] = useState({ name: '', price: '', unit: 'unidade' });

  // Slug gerado
  const slug = formData.business ? generateSlug(formData.business) : 'seu-negocio';
  const fullUrl = `${window.location.origin}/orcamento/${slug}`;

  // Handlers
  const handleInputChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleLogoUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Validar tamanho (máximo 2MB)
      if (file.size > 2 * 1024 * 1024) {
        alert('A imagem deve ter no máximo 2MB');
        return;
      }
      
      // Validar tipo
      if (!file.type.startsWith('image/')) {
        alert('Selecione uma imagem válida');
        return;
      }
      
      const reader = new FileReader();
      reader.onloadend = () => {
        setFormData({
          ...formData,
          logo: reader.result, // Base64
          logoPreview: reader.result
        });
      };
      reader.readAsDataURL(file);
    }
  };

  const handleRemoveLogo = () => {
    setFormData({
      ...formData,
      logo: null,
      logoPreview: null
    });
    if (logoInputRef.current) {
      logoInputRef.current.value = '';
    }
  };

  const handleTemplateSelect = (template) => {
    const isMarcenaria = template.isMarcenaria === true;
    
    setFormData({
      ...formData,
      template: template.id,
      isMarcenaria,
      services: isMarcenaria ? [] : (template.id === 'personalizado' ? [] : [...template.services]),
      marcenariaConfig: isMarcenaria 
        ? {
            ambientes: marcenariaAmbientes.map(a => ({ ...a, ativo: true })),
            materiais: marcenariaMateriais.map(m => ({ ...m, preco: m.precoSugerido, ativo: true })),
            adicionais: marcenariaAdicionais.map(a => ({ ...a, valor: a.valorSugerido, ativo: true })),
            ambientesCustom: [],
            adicionaisCustom: [],
            textoTransparencia: marcenariaTextoTransparencia
          }
        : formData.marcenariaConfig
    });
  };

  const handleAddService = () => {
    if (newService.name && newService.price) {
      setFormData({
        ...formData,
        services: [
          ...formData.services,
          {
            id: Date.now(),
            name: newService.name,
            price: parseFloat(newService.price),
            unit: newService.unit
          }
        ]
      });
      setNewService({ name: '', price: '', unit: 'unidade' });
    }
  };

  const handleRemoveService = (id) => {
    setFormData({
      ...formData,
      services: formData.services.filter(s => s.id !== id)
    });
  };

  const handleUpdateServicePrice = (id, newPrice) => {
    setFormData({
      ...formData,
      services: formData.services.map(s =>
        s.id === id ? { ...s, price: parseFloat(newPrice) || 0 } : s
      )
    });
  };

  // Handlers de Marcenaria
  const handleUpdateMaterialPreco = (id, novoPreco) => {
    setFormData({
      ...formData,
      marcenariaConfig: {
        ...formData.marcenariaConfig,
        materiais: formData.marcenariaConfig.materiais.map(m =>
          m.id === id ? { ...m, preco: parseFloat(novoPreco) || 0 } : m
        )
      }
    });
  };

  const handleToggleMaterial = (id) => {
    setFormData({
      ...formData,
      marcenariaConfig: {
        ...formData.marcenariaConfig,
        materiais: formData.marcenariaConfig.materiais.map(m =>
          m.id === id ? { ...m, ativo: !m.ativo } : m
        )
      }
    });
  };

  const handleUpdateAdicionalValor = (id, novoValor, isCustom = false) => {
    if (isCustom) {
      setFormData({
        ...formData,
        marcenariaConfig: {
          ...formData.marcenariaConfig,
          adicionaisCustom: formData.marcenariaConfig.adicionaisCustom.map(a =>
            a.id === id ? { ...a, valor: parseFloat(novoValor) || 0 } : a
          )
        }
      });
    } else {
      setFormData({
        ...formData,
        marcenariaConfig: {
          ...formData.marcenariaConfig,
          adicionais: formData.marcenariaConfig.adicionais.map(a =>
            a.id === id ? { ...a, valor: parseFloat(novoValor) || 0 } : a
          )
        }
      });
    }
  };

  const handleToggleAdicional = (id, isCustom = false) => {
    if (isCustom) {
      setFormData({
        ...formData,
        marcenariaConfig: {
          ...formData.marcenariaConfig,
          adicionaisCustom: formData.marcenariaConfig.adicionaisCustom.map(a =>
            a.id === id ? { ...a, ativo: !a.ativo } : a
          )
        }
      });
    } else {
      setFormData({
        ...formData,
        marcenariaConfig: {
          ...formData.marcenariaConfig,
          adicionais: formData.marcenariaConfig.adicionais.map(a =>
            a.id === id ? { ...a, ativo: !a.ativo } : a
          )
        }
      });
    }
  };

  const handleToggleAmbiente = (id, isCustom = false) => {
    if (isCustom) {
      setFormData({
        ...formData,
        marcenariaConfig: {
          ...formData.marcenariaConfig,
          ambientesCustom: formData.marcenariaConfig.ambientesCustom.map(a =>
            a.id === id ? { ...a, ativo: !a.ativo } : a
          )
        }
      });
    } else {
      setFormData({
        ...formData,
        marcenariaConfig: {
          ...formData.marcenariaConfig,
          ambientes: formData.marcenariaConfig.ambientes.map(a =>
            a.id === id ? { ...a, ativo: !a.ativo } : a
          )
        }
      });
    }
  };

  // Estado para novos itens customizados
  const [novoAmbiente, setNovoAmbiente] = useState('');
  const [novoAdicional, setNovoAdicional] = useState({ nome: '', tipo: 'ml', valor: '' });

  const handleAddAmbienteCustom = () => {
    if (novoAmbiente.trim()) {
      const id = `custom_${Date.now()}`;
      setFormData({
        ...formData,
        marcenariaConfig: {
          ...formData.marcenariaConfig,
          ambientesCustom: [
            ...formData.marcenariaConfig.ambientesCustom,
            { id, nome: novoAmbiente.trim(), altura: 2.40, fatorTecnico: 2.4, ativo: true, isCustom: true }
          ]
        }
      });
      setNovoAmbiente('');
    }
  };

  const handleAddAdicionalCustom = () => {
    if (novoAdicional.nome.trim() && novoAdicional.valor) {
      const id = `custom_${Date.now()}`;
      setFormData({
        ...formData,
        marcenariaConfig: {
          ...formData.marcenariaConfig,
          adicionaisCustom: [
            ...formData.marcenariaConfig.adicionaisCustom,
            { 
              id, 
              nome: novoAdicional.nome.trim(), 
              tipo: novoAdicional.tipo, 
              valor: parseFloat(novoAdicional.valor), 
              valorSugerido: parseFloat(novoAdicional.valor),
              ativo: true, 
              isCustom: true 
            }
          ]
        }
      });
      setNovoAdicional({ nome: '', tipo: 'ml', valor: '' });
    }
  };

  const handleRemoveCustomItem = (tipo, id) => {
    if (tipo === 'ambiente') {
      setFormData({
        ...formData,
        marcenariaConfig: {
          ...formData.marcenariaConfig,
          ambientesCustom: formData.marcenariaConfig.ambientesCustom.filter(a => a.id !== id)
        }
      });
    } else if (tipo === 'adicional') {
      setFormData({
        ...formData,
        marcenariaConfig: {
          ...formData.marcenariaConfig,
          adicionaisCustom: formData.marcenariaConfig.adicionaisCustom.filter(a => a.id !== id)
        }
      });
    }
  };

  const handleCopyLink = () => {
    navigator.clipboard.writeText(fullUrl);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  // Criar link de pagamento no Asaas
  const handleCreatePaymentLink = async () => {
    setLoading(true);
    setPaymentError(null);
    
    try {
      const response = await axios.post(`${BACKEND_URL}/api/asaas/create-subscription`, {
        customer_name: formData.name,
        customer_email: formData.email,
        customer_phone: formData.phone,
        customer_cpfCnpj: formData.cpfCnpj,
        plan_name: "Orçamento na Hora",
        plan_value: 29.99,
        billing_type: "UNDEFINED",
        cycle: "MONTHLY",
        description: `Orçamento na Hora - ${formData.business}`,
        business_name: formData.business,
        slug: slug
      });
      
      if (response.data.success && response.data.payment_link) {
        setPaymentLink(response.data.payment_link);
      } else {
        setPaymentError(response.data.error || 'Erro ao gerar link de pagamento');
      }
    } catch (error) {
      console.error('Error creating payment link:', error);
      setPaymentError('Erro ao conectar com o sistema de pagamento');
    } finally {
      setLoading(false);
    }
  };

  const handleFinish = async () => {
    // Salva no localStorage para demo
    const professional = {
      ...formData,
      id: Date.now().toString(),
      slug,
      createdAt: new Date().toISOString()
    };
    localStorage.setItem(`orcamento_${slug}`, JSON.stringify(professional));
    
    // Salva no backend
    try {
      await axios.post(`${BACKEND_URL}/api/orcamento/professionals`, {
        name: formData.name,
        business: formData.business,
        phone: formData.phone,
        email: formData.email,
        cpfCnpj: formData.cpfCnpj,
        logo: formData.logo,
        logoPreview: formData.logoPreview,
        template: formData.template,
        services: formData.services,
        isMarcenaria: formData.isMarcenaria,
        marcenariaConfig: formData.marcenariaConfig,
        paymentMethod: formData.paymentMethod,
        pixKey: formData.pixKey,
        pixKeyType: formData.pixKeyType,
        cores: formData.cores || { primary: '#0f172a', secondary: '#334155' }
      });
      console.log('Dados do profissional salvos no backend');
    } catch (error) {
      console.error('Erro ao salvar dados do profissional:', error);
    }
    
    // Inicia trial de 7 dias no backend
    try {
      const response = await axios.post(`${BACKEND_URL}/api/subscriptions/start-trial`, {
        slug: slug,
        business_name: formData.business,
        customer_name: formData.name,
        customer_email: formData.email,
        customer_phone: formData.phone,
        plan_name: "Orçamento na Hora",
        plan_value: 29.99
      });
      
      if (response.data.success) {
        console.log('Trial iniciado:', response.data);
        // Salva info do trial no localStorage
        localStorage.setItem(`orcamento_trial_${slug}`, JSON.stringify({
          trial_ends_at: response.data.trial_ends_at,
          days_left: response.data.days_left
        }));
      }
    } catch (error) {
      console.error('Erro ao iniciar trial:', error);
      // Continua mesmo se falhar - fallback para trial local
    }
    
    setStep(5);
  };

  // Render step 1: Dados básicos
  const renderStep1 = () => (
    <div className="max-w-xl mx-auto">
      <h2 className="text-2xl font-bold text-slate-900 mb-2">Seus dados</h2>
      <p className="text-slate-600 mb-8">Informações que aparecerão no orçamento</p>
      
      <div className="space-y-6">
        <div>
          <Label htmlFor="name">Seu nome</Label>
          <Input
            id="name"
            name="name"
            value={formData.name}
            onChange={handleInputChange}
            placeholder="João Silva"
            className="mt-2"
          />
        </div>
        
        <div>
          <Label htmlFor="business">Nome do negócio</Label>
          <Input
            id="business"
            name="business"
            value={formData.business}
            onChange={handleInputChange}
            placeholder="Montagem Express"
            className="mt-2"
          />
          {formData.business && (
            <p className="text-sm text-slate-500 mt-2">
              Seu link: <span className="text-blue-600 font-medium">{slug}</span>.ativaja.com/orcamento
            </p>
          )}
        </div>

        {/* Upload de Logo */}
        <div>
          <Label>Logo do negócio (aparece no PDF)</Label>
          <div className="mt-2">
            {formData.logoPreview ? (
              <div className="flex items-center gap-4">
                <div className="w-20 h-20 rounded-xl border border-slate-200 overflow-hidden bg-white flex items-center justify-center">
                  <img 
                    src={formData.logoPreview} 
                    alt="Logo" 
                    className="max-w-full max-h-full object-contain"
                  />
                </div>
                <div className="flex flex-col gap-2">
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => logoInputRef.current?.click()}
                  >
                    Trocar logo
                  </Button>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={handleRemoveLogo}
                    className="text-red-600 hover:text-red-700"
                  >
                    <Trash2 size={14} />
                    Remover
                  </Button>
                </div>
              </div>
            ) : (
              <button
                type="button"
                onClick={() => logoInputRef.current?.click()}
                className="w-full p-6 border-2 border-dashed border-slate-300 rounded-xl hover:border-blue-400 hover:bg-blue-50/50 transition-all flex flex-col items-center gap-2"
              >
                <div className="w-12 h-12 rounded-full bg-slate-100 flex items-center justify-center">
                  <Image size={24} className="text-slate-400" />
                </div>
                <span className="text-sm font-medium text-slate-600">Clique para adicionar sua logo</span>
                <span className="text-xs text-slate-400">PNG, JPG ou SVG • Máx. 2MB</span>
              </button>
            )}
            <input
              ref={logoInputRef}
              type="file"
              accept="image/*"
              onChange={handleLogoUpload}
              className="hidden"
            />
          </div>
        </div>
        
        <div>
          <Label htmlFor="phone">WhatsApp</Label>
          <Input
            id="phone"
            name="phone"
            value={formData.phone}
            onChange={handleInputChange}
            placeholder="(24) 99999-9999"
            className="mt-2"
          />
        </div>
        
        <div>
          <Label htmlFor="email">E-mail</Label>
          <Input
            id="email"
            name="email"
            type="email"
            value={formData.email}
            onChange={handleInputChange}
            placeholder="joao@email.com"
            className="mt-2"
          />
        </div>
        
        <div>
          <Label htmlFor="cpfCnpj">CPF ou CNPJ</Label>
          <Input
            id="cpfCnpj"
            name="cpfCnpj"
            value={formData.cpfCnpj}
            onChange={handleInputChange}
            placeholder="000.000.000-00"
            className="mt-2"
          />
          <p className="text-xs text-slate-500 mt-1">Necessário para emissão da nota fiscal</p>
        </div>
      </div>
      
      <div className="mt-8 flex justify-end">
        <Button
          onClick={() => setStep(2)}
          disabled={!formData.name || !formData.business || !formData.phone}
          className="btn-primary"
        >
          Próximo
          <ArrowRight size={18} />
        </Button>
      </div>
    </div>
  );

  // Render step 2: Escolher template
  const renderStep2 = () => (
    <div className="max-w-3xl mx-auto">
      <h2 className="text-2xl font-bold text-slate-900 mb-2">Escolha um modelo</h2>
      <p className="text-slate-600 mb-8">Selecione o tipo de negócio ou crie personalizado</p>
      
      <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
        {businessTemplates.map((template) => {
          const Icon = LucideIcons[template.icon] || LucideIcons.Settings;
          const isSelected = formData.template === template.id;
          
          return (
            <button
              key={template.id}
              onClick={() => handleTemplateSelect(template)}
              className={`p-6 rounded-xl border-2 text-left transition-all ${
                isSelected
                  ? 'border-blue-500 bg-blue-50'
                  : 'border-slate-200 hover:border-blue-300 bg-white'
              }`}
            >
              <div className={`w-12 h-12 rounded-xl flex items-center justify-center mb-4 ${
                isSelected ? 'bg-blue-100' : 'bg-slate-100'
              }`}>
                <Icon size={24} className={isSelected ? 'text-blue-600' : 'text-slate-600'} />
              </div>
              <h3 className="font-semibold text-slate-900 mb-1">{template.name}</h3>
              <p className="text-sm text-slate-500">{template.description}</p>
              {template.services.length > 0 && (
                <p className="text-xs text-blue-600 mt-2">
                  {template.services.length} serviços inclusos
                </p>
              )}
            </button>
          );
        })}
      </div>
      
      <div className="mt-8 flex justify-between">
        <Button variant="outline" onClick={() => setStep(1)}>
          <ArrowLeft size={18} />
          Voltar
        </Button>
        <Button
          onClick={() => setStep(3)}
          disabled={!formData.template}
          className="btn-primary"
        >
          Próximo
          <ArrowRight size={18} />
        </Button>
      </div>
    </div>
  );

  // Render step 3: Configurar serviços (Normal ou Marcenaria)
  const renderStep3 = () => {
    // Se for marcenaria, renderiza configuração especial
    if (formData.isMarcenaria) {
      return renderStep3Marcenaria();
    }
    
    // Configuração normal de serviços
    return (
      <div className="max-w-3xl mx-auto">
        <h2 className="text-2xl font-bold text-slate-900 mb-2">Configure seus serviços</h2>
        <p className="text-slate-600 mb-8">Ajuste os preços ou adicione novos serviços</p>
        
        {/* Lista de serviços */}
        <div className="bg-slate-50 rounded-xl p-6 mb-6">
          <div className="space-y-3">
            {formData.services.map((service) => (
              <div
                key={service.id}
                className="flex items-center gap-4 bg-white p-4 rounded-lg border border-slate-200"
              >
                <div className="flex-1">
                  <p className="font-medium text-slate-800">{service.name}</p>
                  <p className="text-sm text-slate-500">por {service.unit}</p>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-slate-500">R$</span>
                  <Input
                    type="number"
                    value={service.price}
                    onChange={(e) => handleUpdateServicePrice(service.id, e.target.value)}
                    className="w-24"
                  />
                </div>
                <button
                  onClick={() => handleRemoveService(service.id)}
                  className="p-2 text-red-500 hover:bg-red-50 rounded-lg transition-colors"
                >
                  <Trash2 size={18} />
                </button>
              </div>
            ))}
            
            {formData.services.length === 0 && (
              <p className="text-center text-slate-500 py-8">Nenhum serviço cadastrado</p>
            )}
          </div>
        </div>
        
        {/* Adicionar novo serviço */}
        <div className="bg-white rounded-xl p-6 border border-slate-200 mb-8">
          <h3 className="font-semibold text-slate-900 mb-4">Adicionar serviço</h3>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="md:col-span-2">
              <Input
                placeholder="Nome do serviço"
                value={newService.name}
                onChange={(e) => setNewService({ ...newService, name: e.target.value })}
              />
            </div>
            <div>
              <Input
                type="number"
                placeholder="Preço"
                value={newService.price}
                onChange={(e) => setNewService({ ...newService, price: e.target.value })}
              />
            </div>
            <div>
              <select
                value={newService.unit}
                onChange={(e) => setNewService({ ...newService, unit: e.target.value })}
                className="w-full h-10 px-3 rounded-md border border-input bg-background"
              >
                <option value="unidade">por unidade</option>
                <option value="m">por metro</option>
                <option value="m²">por m²</option>
                <option value="hora">por hora</option>
                <option value="serviço">por serviço</option>
                <option value="km">por km</option>
              </select>
            </div>
          </div>
          <Button
            onClick={handleAddService}
            variant="outline"
            className="mt-4"
            disabled={!newService.name || !newService.price}
          >
            <Plus size={18} />
            Adicionar
          </Button>
        </div>
        
        <div className="flex justify-between">
          <Button variant="outline" onClick={() => setStep(2)}>
            <ArrowLeft size={18} />
            Voltar
          </Button>
          <div className="flex gap-3">
            <Button
              variant="outline"
              onClick={() => navigate(`/orcamento/${slug}`)}
            >
              <Eye size={18} />
              Visualizar
            </Button>
            <Button
              onClick={() => setStep(4)}
              disabled={formData.services.length === 0}
              className="btn-primary"
            >
              Próximo
              <ArrowRight size={18} />
            </Button>
          </div>
        </div>
      </div>
    );
  };

  // Render step 3 para Marcenaria
  const renderStep3Marcenaria = () => (
    <div className="max-w-4xl mx-auto">
      <div className="flex items-center gap-3 mb-2">
        <div className="w-10 h-10 rounded-xl bg-amber-100 flex items-center justify-center">
          <Ruler size={20} className="text-amber-600" />
        </div>
        <div>
          <h2 className="text-2xl font-bold text-slate-900">Configure sua tabela de preços</h2>
          <p className="text-slate-600">Sistema de cálculo por metro linear para marcenaria</p>
        </div>
      </div>

      {/* Info Box */}
      <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 mb-8 flex gap-3">
        <Info size={20} className="text-blue-600 flex-shrink-0 mt-0.5" />
        <div className="text-sm text-blue-800">
          <p className="font-medium mb-1">Como funciona o cálculo por metro linear:</p>
          <p>O cliente informa apenas o ambiente e a medida em metros. O sistema calcula automaticamente usando altura padrão técnica (oculta do cliente) e soma os adicionais selecionados.</p>
        </div>
      </div>

      {/* MATERIAIS */}
      <div className="bg-white rounded-2xl border border-slate-200 mb-6 overflow-hidden">
        <div className="bg-slate-50 px-6 py-4 border-b border-slate-200 flex items-center gap-3">
          <Package size={20} className="text-slate-600" />
          <div>
            <h3 className="font-semibold text-slate-900">Materiais e Preços por Metro Linear</h3>
            <p className="text-sm text-slate-500">Configure o preço de cada material (sugestão entre parênteses)</p>
          </div>
        </div>
        <div className="p-6 space-y-3">
          {formData.marcenariaConfig.materiais.map((material) => (
            <div
              key={material.id}
              className={`flex items-center gap-4 p-4 rounded-xl border-2 transition-all ${
                material.ativo 
                  ? 'border-slate-200 bg-white' 
                  : 'border-slate-100 bg-slate-50 opacity-60'
              }`}
            >
              <button
                onClick={() => handleToggleMaterial(material.id)}
                className={`w-6 h-6 rounded-md border-2 flex items-center justify-center transition-all ${
                  material.ativo 
                    ? 'bg-green-500 border-green-500 text-white' 
                    : 'border-slate-300 bg-white'
                }`}
              >
                {material.ativo && <Check size={14} />}
              </button>
              <div className="flex-1">
                <p className="font-medium text-slate-800">{material.nome}</p>
                <p className="text-sm text-slate-500">
                  {material.descricao} 
                  <span className="text-amber-600 ml-2">(sugerido: R$ {material.precoSugerido}/ml)</span>
                </p>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-slate-500">R$</span>
                <Input
                  type="number"
                  value={material.preco}
                  onChange={(e) => handleUpdateMaterialPreco(material.id, e.target.value)}
                  className="w-28"
                  disabled={!material.ativo}
                />
                <span className="text-slate-400 text-sm">/ml</span>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* AMBIENTES */}
      <div className="bg-white rounded-2xl border border-slate-200 mb-6 overflow-hidden">
        <div className="bg-slate-50 px-6 py-4 border-b border-slate-200">
          <h3 className="font-semibold text-slate-900">Ambientes Disponíveis</h3>
          <p className="text-sm text-slate-500">Selecione os ambientes que você atende (altura técnica já configurada)</p>
        </div>
        <div className="p-6">
          <div className="flex flex-wrap gap-3 mb-4">
            {[...formData.marcenariaConfig.ambientes, ...formData.marcenariaConfig.ambientesCustom].map((ambiente) => (
              <div
                key={ambiente.id}
                className={`flex items-center gap-2 px-4 py-2 rounded-full border-2 transition-all cursor-pointer ${
                  ambiente.ativo 
                    ? 'border-green-500 bg-green-50 text-green-700' 
                    : 'border-slate-200 bg-white text-slate-500'
                }`}
                onClick={() => handleToggleAmbiente(ambiente.id, ambiente.isCustom)}
              >
                {ambiente.ativo && <Check size={14} />}
                <span className="font-medium">{ambiente.nome}</span>
                {ambiente.isCustom && (
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      handleRemoveCustomItem('ambiente', ambiente.id);
                    }}
                    className="ml-1 text-red-500 hover:text-red-700"
                  >
                    <Trash2 size={14} />
                  </button>
                )}
              </div>
            ))}
          </div>
          
          {/* Adicionar ambiente customizado */}
          <div className="flex gap-2 mt-4 pt-4 border-t border-slate-100">
            <Input
              placeholder="Adicionar ambiente (ex: Varanda)"
              value={novoAmbiente}
              onChange={(e) => setNovoAmbiente(e.target.value)}
              className="flex-1"
            />
            <Button
              onClick={handleAddAmbienteCustom}
              variant="outline"
              disabled={!novoAmbiente.trim()}
            >
              <Plus size={18} />
              Adicionar
            </Button>
          </div>
        </div>
      </div>

      {/* ADICIONAIS */}
      <div className="bg-white rounded-2xl border border-slate-200 mb-6 overflow-hidden">
        <div className="bg-slate-50 px-6 py-4 border-b border-slate-200 flex items-center gap-3">
          <Sparkles size={20} className="text-slate-600" />
          <div>
            <h3 className="font-semibold text-slate-900">Adicionais (Opcionais do Cliente)</h3>
            <p className="text-sm text-slate-500">Itens extras que o cliente pode selecionar (sugestão entre parênteses)</p>
          </div>
        </div>
        <div className="p-6 space-y-3">
          {[...formData.marcenariaConfig.adicionais, ...formData.marcenariaConfig.adicionaisCustom].map((adicional) => (
            <div
              key={adicional.id}
              className={`flex items-center gap-4 p-4 rounded-xl border-2 transition-all ${
                adicional.ativo 
                  ? 'border-slate-200 bg-white' 
                  : 'border-slate-100 bg-slate-50 opacity-60'
              }`}
            >
              <button
                onClick={() => handleToggleAdicional(adicional.id, adicional.isCustom)}
                className={`w-6 h-6 rounded-md border-2 flex items-center justify-center transition-all ${
                  adicional.ativo 
                    ? 'bg-green-500 border-green-500 text-white' 
                    : 'border-slate-300 bg-white'
                }`}
              >
                {adicional.ativo && <Check size={14} />}
              </button>
              <div className="flex-1">
                <p className="font-medium text-slate-800">{adicional.nome}</p>
                <p className="text-sm text-slate-500">
                  {adicional.descricao || (adicional.tipo === 'percentual' ? 'Percentual sobre o valor base' : 'Valor por metro linear')}
                  {adicional.valorSugerido && (
                    <span className="text-amber-600 ml-2">
                      (sugerido: {adicional.tipo === 'percentual' ? `${adicional.valorSugerido}%` : `R$ ${adicional.valorSugerido}/ml`})
                    </span>
                  )}
                </p>
              </div>
              <div className="flex items-center gap-2">
                {adicional.tipo === 'percentual' ? (
                  <>
                    <span className="text-slate-500">+</span>
                    <Input
                      type="number"
                      value={adicional.valor}
                      onChange={(e) => handleUpdateAdicionalValor(adicional.id, e.target.value, adicional.isCustom)}
                      className="w-20"
                      disabled={!adicional.ativo}
                    />
                    <span className="text-slate-400 text-sm">%</span>
                  </>
                ) : (
                  <>
                    <span className="text-slate-500">R$</span>
                    <Input
                      type="number"
                      value={adicional.valor}
                      onChange={(e) => handleUpdateAdicionalValor(adicional.id, e.target.value, adicional.isCustom)}
                      className="w-24"
                      disabled={!adicional.ativo}
                    />
                    <span className="text-slate-400 text-sm">/ml</span>
                  </>
                )}
                {adicional.isCustom && (
                  <button
                    onClick={() => handleRemoveCustomItem('adicional', adicional.id)}
                    className="p-2 text-red-500 hover:bg-red-50 rounded-lg"
                  >
                    <Trash2 size={16} />
                  </button>
                )}
              </div>
            </div>
          ))}
          
          {/* Adicionar adicional customizado */}
          <div className="mt-4 pt-4 border-t border-slate-100">
            <p className="text-sm text-slate-600 mb-3">Adicionar item extra:</p>
            <div className="flex gap-2 flex-wrap">
              <Input
                placeholder="Nome do adicional"
                value={novoAdicional.nome}
                onChange={(e) => setNovoAdicional({ ...novoAdicional, nome: e.target.value })}
                className="flex-1 min-w-[200px]"
              />
              <select
                value={novoAdicional.tipo}
                onChange={(e) => setNovoAdicional({ ...novoAdicional, tipo: e.target.value })}
                className="h-10 px-3 rounded-md border border-input bg-background"
              >
                <option value="ml">R$ por metro linear</option>
                <option value="percentual">% sobre valor base</option>
              </select>
              <Input
                type="number"
                placeholder={novoAdicional.tipo === 'percentual' ? '% ex: 15' : 'R$ ex: 150'}
                value={novoAdicional.valor}
                onChange={(e) => setNovoAdicional({ ...novoAdicional, valor: e.target.value })}
                className="w-32"
              />
              <Button
                onClick={handleAddAdicionalCustom}
                variant="outline"
                disabled={!novoAdicional.nome.trim() || !novoAdicional.valor}
              >
                <Plus size={18} />
                Adicionar
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Botões de navegação */}
      <div className="flex justify-between">
        <Button variant="outline" onClick={() => setStep(2)}>
          <ArrowLeft size={18} />
          Voltar
        </Button>
        <div className="flex gap-3">
          <Button
            variant="outline"
            onClick={() => navigate(`/orcamento/${slug}`)}
          >
            <Eye size={18} />
            Visualizar
          </Button>
          <Button
            onClick={() => setStep(4)}
            disabled={!formData.marcenariaConfig.materiais.some(m => m.ativo)}
            className="btn-primary"
          >
            Próximo
            <ArrowRight size={18} />
          </Button>
        </div>
      </div>
    </div>
  );

  // Render step 4: Forma de recebimento
  const renderStep4 = () => (
    <div className="max-w-xl mx-auto">
      <h2 className="text-2xl font-bold text-slate-900 mb-2">Como você quer receber dos clientes?</h2>
      <p className="text-slate-600 mb-8">Configure como seus clientes vão pagar após aprovar o orçamento</p>
      
      <div className="space-y-4 mb-8">
        {/* Opção Pix */}
        <button
          onClick={() => setFormData({ ...formData, paymentMethod: 'pix' })}
          className={`w-full p-6 rounded-xl border-2 text-left transition-all ${
            formData.paymentMethod === 'pix'
              ? 'border-blue-500 bg-blue-50'
              : 'border-slate-200 hover:border-blue-300'
          }`}
        >
          <div className="flex items-center gap-4">
            <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${
              formData.paymentMethod === 'pix' ? 'bg-blue-100' : 'bg-slate-100'
            }`}>
              <span className="text-2xl">💰</span>
            </div>
            <div>
              <h3 className="font-semibold text-slate-900">Pix</h3>
              <p className="text-sm text-slate-500">Cadastre sua chave Pix. Taxa: 0%</p>
            </div>
          </div>
        </button>

        {/* Opção Asaas */}
        <button
          onClick={() => setFormData({ ...formData, paymentMethod: 'asaas' })}
          className={`w-full p-6 rounded-xl border-2 text-left transition-all ${
            formData.paymentMethod === 'asaas'
              ? 'border-blue-500 bg-blue-50'
              : 'border-slate-200 hover:border-blue-300'
          }`}
        >
          <div className="flex items-center gap-4">
            <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${
              formData.paymentMethod === 'asaas' ? 'bg-blue-100' : 'bg-slate-100'
            }`}>
              <span className="text-2xl">💳</span>
            </div>
            <div>
              <h3 className="font-semibold text-slate-900">Cartão + Pix (Asaas)</h3>
              <p className="text-sm text-slate-500">Aceite cartão de crédito e Pix automático. Taxa: ~3%</p>
            </div>
          </div>
        </button>

        {/* Opção Presencial */}
        <button
          onClick={() => setFormData({ ...formData, paymentMethod: 'presencial' })}
          className={`w-full p-6 rounded-xl border-2 text-left transition-all ${
            formData.paymentMethod === 'presencial'
              ? 'border-blue-500 bg-blue-50'
              : 'border-slate-200 hover:border-blue-300'
          }`}
        >
          <div className="flex items-center gap-4">
            <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${
              formData.paymentMethod === 'presencial' ? 'bg-blue-100' : 'bg-slate-100'
            }`}>
              <span className="text-2xl">🏪</span>
            </div>
            <div>
              <h3 className="font-semibold text-slate-900">Maquininha / Presencial</h3>
              <p className="text-sm text-slate-500">Cliente paga direto com você. Sem integração.</p>
            </div>
          </div>
        </button>
      </div>

      {/* Campos adicionais para Pix */}
      {formData.paymentMethod === 'pix' && (
        <div className="bg-slate-50 rounded-xl p-6 mb-8">
          <h3 className="font-semibold text-slate-900 mb-4">Configure sua chave Pix</h3>
          <div className="space-y-4">
            <div>
              <Label>Tipo da chave</Label>
              <select
                value={formData.pixKeyType}
                onChange={(e) => setFormData({ ...formData, pixKeyType: e.target.value })}
                className="w-full h-10 px-3 mt-2 rounded-md border border-input bg-background"
              >
                <option value="cpf">CPF</option>
                <option value="cnpj">CNPJ</option>
                <option value="email">E-mail</option>
                <option value="phone">Telefone</option>
                <option value="random">Chave aleatória</option>
              </select>
            </div>
            <div>
              <Label>Chave Pix</Label>
              <Input
                value={formData.pixKey}
                onChange={(e) => setFormData({ ...formData, pixKey: e.target.value })}
                placeholder={
                  formData.pixKeyType === 'cpf' ? '000.000.000-00' :
                  formData.pixKeyType === 'cnpj' ? '00.000.000/0000-00' :
                  formData.pixKeyType === 'email' ? 'seu@email.com' :
                  formData.pixKeyType === 'phone' ? '(00) 00000-0000' :
                  'Cole sua chave aleatória'
                }
                className="mt-2"
              />
            </div>
            <div>
              <Label>Nome do titular (como aparece no Pix)</Label>
              <Input
                value={formData.pixName || formData.name}
                onChange={(e) => setFormData({ ...formData, pixName: e.target.value })}
                placeholder="Nome que aparece ao pagar"
                className="mt-2"
              />
            </div>
          </div>
        </div>
      )}

      {/* Campos adicionais para Asaas */}
      {formData.paymentMethod === 'asaas' && (
        <div className="bg-blue-50 rounded-xl p-6 mb-8 border border-blue-200">
          <h3 className="font-semibold text-slate-900 mb-2">Conta Asaas</h3>
          <p className="text-sm text-slate-600 mb-4">
            Para receber por cartão, você precisa de uma conta no Asaas (gratuita).
          </p>
          <div className="space-y-3">
            <a
              href="https://www.asaas.com/criar-conta"
              target="_blank"
              rel="noopener noreferrer"
              className="block w-full text-center bg-blue-600 hover:bg-blue-700 text-white py-3 rounded-lg font-medium transition-colors"
            >
              Criar conta gratuita no Asaas →
            </a>
            <p className="text-xs text-slate-500 text-center">
              Já tem conta? Cole sua chave API abaixo (opcional - configuramos depois)
            </p>
            <Input
              value={formData.asaasAccount}
              onChange={(e) => setFormData({ ...formData, asaasAccount: e.target.value })}
              placeholder="Chave API do Asaas (opcional)"
            />
          </div>
        </div>
      )}

      {/* Info Presencial */}
      {formData.paymentMethod === 'presencial' && (
        <div className="bg-slate-50 rounded-xl p-6 mb-8">
          <h3 className="font-semibold text-slate-900 mb-2">Pagamento presencial</h3>
          <p className="text-sm text-slate-600">
            Seus clientes verão a mensagem para combinar o pagamento diretamente com você 
            (maquininha, dinheiro, transferência, etc).
          </p>
        </div>
      )}

      <div className="flex justify-between">
        <Button variant="outline" onClick={() => setStep(3)}>
          <ArrowLeft size={18} />
          Voltar
        </Button>
        <Button
          onClick={handleFinish}
          disabled={!formData.paymentMethod || (formData.paymentMethod === 'pix' && !formData.pixKey)}
          className="btn-primary"
        >
          <Save size={18} />
          Finalizar
        </Button>
      </div>
    </div>
  );

  // Render step 5: Sucesso
  const renderStep5 = () => (
    <div className="max-w-xl mx-auto text-center">
      <div className="w-20 h-20 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-6">
        <Check size={40} className="text-green-600" />
      </div>
      
      <h2 className="text-2xl font-bold text-slate-900 mb-2">Configuração concluída!</h2>
      <p className="text-slate-600 mb-8">
        Seu sistema de orçamentos está pronto para ser ativado.
      </p>
      
      {/* Link preview */}
      <div className="bg-slate-50 rounded-xl p-6 mb-6">
        <p className="text-sm text-slate-500 mb-2">Seu link exclusivo será:</p>
        <div className="flex items-center gap-2">
          <input
            type="text"
            value={fullUrl}
            readOnly
            className="flex-1 px-4 py-3 bg-white rounded-lg border border-slate-200 text-slate-800 text-sm"
          />
          <Button onClick={handleCopyLink} variant="outline">
            {copied ? <Check size={18} /> : <Copy size={18} />}
          </Button>
        </div>
      </div>

      {/* Testar demo */}
      <div className="flex flex-col sm:flex-row gap-4 justify-center mb-8">
        <Button onClick={() => navigate(`/orcamento/${slug}`)} variant="outline">
          <Eye size={18} />
          Testar meu orçamento
        </Button>
      </div>
      
      {/* Box de ativação */}
      <div className="bg-gradient-to-br from-blue-600 to-blue-700 rounded-2xl p-8 text-white">
        <div className="w-12 h-12 rounded-full bg-white/20 flex items-center justify-center mx-auto mb-4">
          <Zap size={24} className="text-white" />
        </div>
        <h3 className="text-xl font-bold mb-2">Ative seu link agora</h3>
        <p className="text-blue-100 text-sm mb-6">
          Para liberar seu link personalizado e começar a receber orçamentos dos clientes, 
          ative sua assinatura.
        </p>
        
        <div className="bg-white/10 rounded-xl p-4 mb-6">
          <div className="flex items-center justify-between mb-2">
            <span className="text-blue-100">Assinatura mensal</span>
            <span className="text-2xl font-bold">R$ 29,99</span>
          </div>
          <ul className="text-left text-sm text-blue-100 space-y-1">
            <li className="flex items-center gap-2">
              <Check size={14} /> Link ativado permanentemente
            </li>
            <li className="flex items-center gap-2">
              <Check size={14} /> Orçamentos ilimitados
            </li>
            <li className="flex items-center gap-2">
              <Check size={14} /> Cobrança automática
            </li>
            <li className="flex items-center gap-2">
              <Check size={14} /> Cancele quando quiser
            </li>
          </ul>
        </div>
        
        {/* Botão de pagamento */}
        {paymentLink ? (
          <div className="space-y-3">
            <a
              href={paymentLink}
              target="_blank"
              rel="noopener noreferrer"
              className="w-full bg-green-500 hover:bg-green-400 text-white font-semibold py-3 px-6 rounded-full inline-flex items-center justify-center gap-2 transition-colors"
            >
              <ExternalLink size={18} />
              Pagar agora - R$ 29,99
            </a>
            <p className="text-xs text-green-200 mt-2">
              ✓ Link de pagamento gerado com sucesso!
            </p>
            <button
              onClick={() => navigate(`/assinatura/${slug}`)}
              className="text-xs text-white/70 hover:text-white mt-2 underline"
            >
              Gerenciar minha assinatura →
            </button>
          </div>
        ) : (
          <Button
            className="w-full bg-white text-blue-600 hover:bg-blue-50 font-semibold py-3 disabled:opacity-70"
            onClick={handleCreatePaymentLink}
            disabled={loading}
          >
            {loading ? (
              <>
                <Loader2 size={18} className="animate-spin" />
                Gerando link...
              </>
            ) : (
              <>
                <Zap size={18} />
                Gerar link de pagamento
              </>
            )}
          </Button>
        )}
        
        {paymentError && (
          <p className="text-xs text-red-200 mt-3">
            Erro: {paymentError}
          </p>
        )}
        
        <p className="text-xs text-blue-200 mt-4">
          Pagamento via Pix, cartão ou boleto • Ativação automática
        </p>
      </div>
      
      {/* Nota de teste */}
      <div className="mt-8 p-4 bg-amber-50 rounded-xl border border-amber-200">
        <p className="text-sm text-amber-800">
          <strong>💡 Período de teste:</strong> Você tem 7 dias para testar gratuitamente. 
          Após esse período, o link só ficará disponível para seus clientes após o pagamento da assinatura.
        </p>
      </div>
      
      {/* Link para gerenciar assinatura */}
      <div className="mt-4 text-center">
        <button
          onClick={() => navigate(`/assinatura/${slug}`)}
          className="text-sm text-slate-500 hover:text-slate-700 underline"
        >
          Gerenciar minha assinatura / Cancelar
        </button>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <header className="border-b border-slate-200 bg-white">
        <div className="max-w-5xl mx-auto px-4 h-16 flex items-center justify-between">
          <a href="/" className="flex items-center gap-2">
            <img src={siteData.brand.logo} alt="AtivaJá" className="h-8" />
            <span className="text-slate-400">|</span>
            <span className="font-medium text-slate-700">Orçamento na Hora</span>
          </a>
          
          {step < 5 && (
            <div className="flex items-center gap-2">
              {[1, 2, 3, 4].map((s) => (
                <div
                  key={s}
                  className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${
                    s === step
                      ? 'bg-blue-600 text-white'
                      : s < step
                      ? 'bg-blue-100 text-blue-600'
                      : 'bg-slate-100 text-slate-400'
                  }`}
                >
                  {s < step ? <Check size={16} /> : s}
                </div>
              ))}
            </div>
          )}
        </div>
      </header>

      {/* Content */}
      <main className="py-12 px-4">
        {step === 1 && renderStep1()}
        {step === 2 && renderStep2()}
        {step === 3 && renderStep3()}
        {step === 4 && renderStep4()}
        {step === 5 && renderStep5()}
      </main>

      {/* Master Access - discreto no canto inferior direito */}
      <MasterAccess 
        produto="orcamento" 
        slug={slug}
        onSuccess={() => navigate(`/orcamento/${slug}`)}
      />
    </div>
  );
};

export default OrcamentoConfigurar;
